import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/authStore";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export const api = axios.create({ baseURL, timeout: 30_000 });

api.interceptors.request.use((cfg) => {
  const token = useAuthStore.getState().accessToken;
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const { refreshToken, setSession, clear, user } =
      useAuthStore.getState();
    if (!refreshToken) {
      clear();
      return null;
    }
    try {
      const r = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
      setSession({
        accessToken: r.data.accessToken,
        accessTokenExpiresAt: r.data.accessTokenExpiresAt,
        refreshToken: r.data.refreshToken,
        user: user!,
      });
      return r.data.accessToken as string;
    } catch {
      clear();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const status = error.response?.status;
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !original.url?.includes("/auth/refresh")
    ) {
      original._retried = true;
      const fresh = await refreshAccessToken();
      if (fresh) {
        original.headers.Authorization = `Bearer ${fresh}`;
        return api.request(original);
      }
      window.location.assign(
        `/login?next=${encodeURIComponent(window.location.pathname)}`,
      );
    }
    return Promise.reject(error);
  },
);
